import React, { useEffect, useState } from 'react'
import { useSelector } from 'hooks/redux'
import PropTypes from 'prop-types'
import { isEmpty, prop, assoc } from 'ramda'
import strings from 'utils/strings'
import { chunkBy } from 'utils/functional'
import { FEATURE_SINGULAR, ADD_REMOVE_FEATURES } from 'utils/constants'
import { selectVehicleBySerial } from 'selectors/truck'
import { getCollectionsAsList, getIsLoading } from 'selectors/collection'
// Bug 538201: getCanViewCollectionRevision removed — Card always shows status

import {
  getCollections as fetchCollections,
  getCollection as fetchCollection,
} from 'actions/collection'
import { getGroup as fetchCollectionDetail } from 'services/collections'
import { useDispatch } from 'react-redux'
import * as yup from 'yup'
import { Loading, Button, SelectMenu, Checkbox } from 'components/patterns'
import { setSelectedIdWithDrawer } from 'actions/navigator'
import { getTruckModules } from 'actions/truck'
import EmptyCompartment from 'img/Empty-compartment.svg'
import SearchInputV2 from 'components/patterns/Inputs/SearchInputV2'
import {
  SearchContainer,
  CollectionsContainer,
  LoadingContainer,
  CollectionSearch,
  SearchInputContainer,
  StyledCollectionCard,
  StyledSearchSortLabel,
  SortContainer,
  FilterContainer,
  StyledVerticalDivider,
} from './Search.style'
import { getFilteredCollectionsList } from './searchFilters'
import FeatureCollectionToggle from '../FeatureCollectionToggle'
import {
  ContentWrapper,
  ContentContainer,
  Footer,
  InnerContentContainer,
} from './FeatureCollections.style'

const Search = ({ serialNumber, reset, changeStep }) => {
  const dispatch = useDispatch()
  const SortOptions = {
    'timeStamp-desc': [
      strings.feature_collection_search_sort_label_newest_first,
      (a, b) => new Date(b.timeStamp) - new Date(a.timeStamp),
    ],
    'timeStamp-asc': [
      strings.feature_collection_search_sort_label_oldest_first,
      (a, b) => new Date(a.timeStamp) - new Date(b.timeStamp),
    ],
    'title-asc': [
      strings.feature_collection_search_sort_label_title_asc,
      (a, b) => a.name.localeCompare(b.name),
    ],
    'title-desc': [
      strings.feature_collection_search_sort_label_title_desc,
      (a, b) => b.name.localeCompare(a.name),
    ],
  }
  const SortMenuOptions = Object.entries(SortOptions).map(([key, [title]]) => ({
    key,
    value: key,
    title,
  }))
  const [{ value: DefaultSortOption }] = SortMenuOptions
  const [searchValue, setSearchValue] = useState('')
  const [collections, setCollections] = useState([])
  const [collectionSort, updateCollectionSort] = useState(DefaultSortOption)
  const [filteredCollections, setFilteredCollections] = useState([])
  const [showCompatibleOnly, setShowCompatibleOnly] = useState(true)
  // Keyed by collection id. Holds varMinCompat fetched from the detail endpoint
  // for collections whose list-API response omitted the field (null/undefined).
  const [enrichedVariants, setEnrichedVariants] = useState({})

  const vehicle = useSelector(selectVehicleBySerial, { serialNumber })
  const groups = useSelector(getCollectionsAsList)
  const isLoading = useSelector(getIsLoading)

  // Ensure ECU modules (including ecuVariant) are loaded so the App Variant
  // compatibility check has the data it needs. The modules endpoint is not
  // fetched on initial vehicle load — only after feature saves — so we fetch
  // it here when the collections search mounts.
  useEffect(() => {
    if (serialNumber) {
      dispatch(getTruckModules({ serialNumber }))
    }
  }, [serialNumber, dispatch])

  // When the compatible-only filter is active, some collections arrive from the
  // list API without a varMinCompat value (the field is absent or null). The
  // detail endpoint always includes it. Fetch details for those collections so
  // the compatibility check has the data it needs to filter them correctly.
  useEffect(() => {
    if (!showCompatibleOnly || !collections.length) return undefined

    const missing = collections.filter(
      (c) => c.varMinCompat == null && enrichedVariants[c.id] === undefined
    )
    if (!missing.length) return undefined

    let cancelled = false

    missing.forEach((c) => {
      fetchCollectionDetail({ id: c.id })
        .then((response) => {
          if (cancelled) return
          const varMinCompat = response?.data?.varMinCompat ?? null
          setEnrichedVariants((prev) => ({ ...prev, [c.id]: varMinCompat }))
        })
        .catch(() => {
          // On fetch failure keep the entry as null so we don't retry endlessly.
          if (!cancelled) {
            setEnrichedVariants((prev) => ({ ...prev, [c.id]: null }))
          }
        })
    })

    return () => {
      cancelled = true
    }
  }, [showCompatibleOnly, collections]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const [, sortComparator] = SortOptions[collectionSort]
    // Merge any enriched varMinCompat values into the collection objects before
    // filtering. Collections that had null varMinCompat in the list API response
    // will now carry the value fetched from the detail endpoint.
    const enriched = collections.map((c) => {
      if (c.varMinCompat == null && enrichedVariants[c.id] !== undefined) {
        return { ...c, varMinCompat: enrichedVariants[c.id] }
      }
      return c
    })
    setFilteredCollections(
      getFilteredCollectionsList(
        enriched,
        searchValue,
        sortComparator,
        showCompatibleOnly,
        vehicle
      )
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    searchValue,
    collections,
    collectionSort,
    showCompatibleOnly,
    vehicle,
    enrichedVariants,
  ])

  const vehicleSubType = vehicle?.subType

  useEffect(() => {
    if (!groups && !isLoading) {
      dispatch(fetchCollections({ groupType: FEATURE_SINGULAR }))
    }
    if (groups !== null && !isEmpty(groups)) {
      setCollections(groups.map(assoc('show', true)))
    }
  }, [groups, vehicleSubType, dispatch, isLoading])

  const handleCollectionSelection = (collection) => {
    dispatch(fetchCollection(collection, serialNumber))
    changeStep(1)
  }

  const handleCancel = () => {
    reset()
    dispatch(setSelectedIdWithDrawer(ADD_REMOVE_FEATURES))
  }

  const renderCollectionCards = () => {
    if (filteredCollections && !isEmpty(filteredCollections)) {
      const cards = filteredCollections.reduce((acc, collection) => {
        const shouldShow = prop('show', collection)
        if (shouldShow) {
          return [
            ...acc,
            <StyledCollectionCard
              key={`collection-${prop('id', collection)}`}
              className={`collection-${prop('id', collection)}`}
              collection={collection}
              handleSelection={() => {
                handleCollectionSelection(collection)
              }}
              vehicle={vehicle}
            />,
          ]
        }
        return acc
      }, [])

      const chunks = chunkBy(2, cards)

      const withRows = chunks.map((chunk) => chunk)
      return withRows
    }

    return null
  }

  const handleOnClear = () => {
    setSearchValue('')
  }

  return (
    <ContentWrapper>
      <ContentContainer>
        <div>
          <CollectionSearch id="features-collection-search">
            <div>
              <FeatureCollectionToggle serialNumber={serialNumber} />
            </div>
            <SearchContainer isLoading={isLoading}>
              <SearchInputContainer>
                <SearchInputV2
                  value={searchValue}
                  onClear={handleOnClear}
                  onChange={(e) => setSearchValue(e?.target?.value)}
                  placeholder={strings.search_feature_collections}
                />
              </SearchInputContainer>
              <FilterContainer>
                <Checkbox
                  checked={showCompatibleOnly}
                  onChange={(e) => setShowCompatibleOnly(e.target.checked)}
                >
                  {strings.only_show_compatible_collections}
                </Checkbox>
                <StyledVerticalDivider type="vertical" />
                <SortContainer
                  type="inline"
                  justify="space-around"
                  style={{ marginLeft: 0, marginRight: 0 }}
                >
                  <StyledSearchSortLabel>
                    {strings.feature_collection_search_sort_by}
                  </StyledSearchSortLabel>
                  <SelectMenu
                    options={SortMenuOptions}
                    onChange={(v) => {
                      if (v) updateCollectionSort(v)
                    }}
                    name="collectionSortOptions"
                    value={DefaultSortOption}
                    rules={yup.string().nullable()}
                    horizontal
                    width={100}
                  />
                </SortContainer>
              </FilterContainer>
            </SearchContainer>
          </CollectionSearch>
        </div>
        {!isLoading && isEmpty(filteredCollections) && (
          <InnerContentContainer>
            <img src={EmptyCompartment} alt="Empty compartment" />
            <StyledSearchSortLabel>
              {strings.empty_feature_collections}
            </StyledSearchSortLabel>
          </InnerContentContainer>
        )}
        {isLoading ? (
          <LoadingContainer>
            <Loading type="sm" />
          </LoadingContainer>
        ) : (
          <CollectionsContainer>{renderCollectionCards()}</CollectionsContainer>
        )}
      </ContentContainer>

      <Footer>
        <Button text={strings.cancel} onClick={handleCancel} type="secondary" />
      </Footer>
    </ContentWrapper>
  )
}

Search.propTypes = {
  serialNumber: PropTypes.string,
  reset: PropTypes.func,
  changeStep: PropTypes.func,
  step: PropTypes.number,
}

export default Search
